import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { History } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable, TruncCell } from "@/components/common/DataTable";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SuperadminAuditAction } from "@/lib/superadminAudit";

interface AuditEntry {
  id: string;
  created_at: string;
  actor_user_id: string;
  actor_email: string | null;
  action: SuperadminAuditAction;
  target_company_id: string | null;
  target_company_name: string | null;
  target_user_id: string | null;
  target_user_email: string | null;
  metadata: Record<string, unknown> | null;
}

type AuditFilter = "all" | "company" | "admin" | "superadmin" | "users" | "switch" | "workOrders";
const AUDIT_PAGE_SIZE = 100;

const ACTION_LABELS: Record<SuperadminAuditAction, string> = {
  company_created: "Company created",
  company_deleted: "Company deleted",
  company_renamed: "Company renamed",
  admin_assigned: "Admin assigned",
  superadmin_granted: "Superadmin granted",
  superadmin_revoked: "Superadmin revoked",
  user_deleted: "User account deleted",
  tenant_switched: "Tenant switched",
  work_order_deleted: "Work order deleted",
};

const ACTION_FILTER: Record<AuditFilter, SuperadminAuditAction[] | null> = {
  all: null,
  company: ["company_created", "company_deleted", "company_renamed"],
  admin: ["admin_assigned"],
  superadmin: ["superadmin_granted", "superadmin_revoked"],
  users: ["user_deleted"],
  switch: ["tenant_switched"],
  workOrders: ["work_order_deleted"],
};


const targetOf = (e: AuditEntry) =>
  e.target_company_name
    ? e.target_user_email ? `${e.target_user_email} → ${e.target_company_name}` : e.target_company_name
    : e.target_user_email ?? "—";

export default function AuditLog() {
  const { isSuperadmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();

  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditFilter, setAuditFilter] = useState<AuditFilter>("all");
  const [auditLimit, setAuditLimit] = useState(AUDIT_PAGE_SIZE);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadAuditLog = async (limit: number = auditLimit) => {
    setAuditLoading(true);
    try {
      const { data, error } = await supabase
        .from("superadmin_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit + 1);
      if (error) throw error;
      const rows = (data ?? []) as unknown as AuditEntry[];
      setAuditHasMore(rows.length > limit);
      setAuditEntries(rows.slice(0, limit));
    } catch (err) {
      console.warn("[audit] load failed", err);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperadmin) return;
    loadAuditLog(AUDIT_PAGE_SIZE);
  }, [isSuperadmin]);

  useEffect(() => {
    if (!isSuperadmin) return;
    const channel = supabase
      .channel("superadmin_audit_log_changes_page")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "superadmin_audit_log" },
        (payload) => {
          setAuditEntries((prev) => [payload.new as AuditEntry, ...prev]);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isSuperadmin]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!isSuperadmin) return <Navigate to="/settings/profile" replace />;

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <History className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
        </div>
        <p className="text-muted-foreground">Platform-wide record of superadmin actions</p>
      </div>

      <section className="px-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{auditEntries.length}</Badge>
          </div>
          <Select value={auditFilter} onValueChange={(v) => setAuditFilter(v as AuditFilter)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="company">Companies</SelectItem>
              <SelectItem value="admin">Admin assignments</SelectItem>
              <SelectItem value="superadmin">Superadmin changes</SelectItem>
              <SelectItem value="users">User deletions</SelectItem>
              <SelectItem value="switch">Tenant switches</SelectItem>
              <SelectItem value="workOrders">Work orders</SelectItem>

            </SelectContent>
          </Select>
        </div>
        <DataTable<AuditEntry>
          storageKey="audit-log-column-widths"
          rows={ACTION_FILTER[auditFilter] ? auditEntries.filter((e) => ACTION_FILTER[auditFilter]!.includes(e.action)) : auditEntries}
          rowKey={(e) => e.id}
          loading={auditLoading}
          defaultSort={{ key: "when", asc: false }}
          columns={[
            { key: "when", header: "When", width: 170, sortValue: (e) => e.created_at,
              cell: (e) => <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span> },
            { key: "actor", header: "Actor", width: 220, sortValue: (e) => e.actor_email ?? e.actor_user_id,
              cell: (e) => <TruncCell className="text-sm">{e.actor_email ?? e.actor_user_id}</TruncCell> },
            { key: "action", header: "Action", width: 200, sortValue: (e) => ACTION_LABELS[e.action] ?? e.action,
              cell: (e) => <Badge variant={e.action.includes("deleted") || e.action.includes("revoked") ? "destructive" : "outline"} className="whitespace-nowrap">{ACTION_LABELS[e.action] ?? e.action}</Badge> },
            { key: "target", header: "Target", width: 260, sortValue: (e) => targetOf(e),
              cell: (e) => <TruncCell className="text-sm">{targetOf(e)}</TruncCell> },
            { key: "details", header: "Details", width: 280,
              cell: (e) => {
                const has = e.metadata && Object.keys(e.metadata).length > 0;
                if (!has) return <span className="text-muted-foreground">—</span>;
                return (
                  <HoverCard openDelay={120} closeDelay={80}>
                    <HoverCardTrigger asChild>
                      <span tabIndex={0} className="block truncate text-xs text-muted-foreground font-mono cursor-default outline-none">{JSON.stringify(e.metadata)}</span>
                    </HoverCardTrigger>
                    <HoverCardContent align="start" className="w-96 p-3">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-all max-h-80 overflow-auto">{JSON.stringify(e.metadata, null, 2)}</pre>
                    </HoverCardContent>
                  </HoverCard>
                );
              } },
          ]}
          footer={auditHasMore ? (
            <div className="p-3 border-t border-border flex justify-center">
              <Button variant="outline" size="sm" disabled={auditLoading}
                onClick={() => { const next = auditLimit + AUDIT_PAGE_SIZE; setAuditLimit(next); loadAuditLog(next); }}>
                Load more
              </Button>
            </div>
          ) : undefined}
        />
      </section>
    </div>
  );
}
